import { useOrderSummary } from '@/features/orders/hooks/useOrders';
import { useT } from '@/i18n';

/**
 * Live "situation" strip in the orders navbar's <Subnavbar>. Every tile answers
 * "what needs me right now?":
 *   • Needs action - orders awaiting an owner decision (PENDING)
 *   • Active       - orders accepted and being prepared (CONFIRMED)
 *   • Overdue      - the subset of Active past its promised ready time. Shown
 *                    ONLY when > 0 (red), so the strip stays calm when on time
 *                    and shouts only when the kitchen is slipping.
 *
 * Point-in-time counts of transient states, fetched as real grouped COUNTs -
 * never a capped list page. Historical totals (completed/cancelled) and money
 * (revenue) live in Reports, not here: on a live triage screen they drive no
 * action and only add noise.
 */
// A stable `key` (never localized) drives both the count lookup and the styling;
// the visible label is resolved from `t.orders.summaryBar` inside the component.
type CellKey = 'pending' | 'active' | 'overdue';
type Cell = { key: CellKey; color: string };

const BASE_CELLS: Cell[] = [
  { key: 'pending', color: '#ff9500' },
  { key: 'active', color: '#007aff' },
];
const OVERDUE_CELL: Cell = { key: 'overdue', color: '#ff3b30' };

export default function OrderSummaryBar() {
  const t = useT();
  const { data } = useOrderSummary();

  const overdue = data?.overdue ?? 0;
  const cells = overdue > 0 ? [...BASE_CELLS, OVERDUE_CELL] : BASE_CELLS;

  return (
    <div className="ord-summary-bar">
      {cells.map((c) => {
        const value = data?.[c.key] ?? 0;
        // Overdue is never dim - when it's shown it always demands attention.
        const dim = c.key !== 'overdue' && value === 0;
        return (
          <div key={c.key} className="ord-summary-cell">
            <div
              style={{
                fontSize: 19,
                fontWeight: 700,
                lineHeight: 1.1,
                color: dim ? '#c7c7cc' : c.color,
              }}
            >
              {value}
            </div>
            <div
              style={{
                fontSize: 10,
                color: c.key === 'overdue' ? c.color : '#8e8e93',
                textTransform: 'uppercase',
                letterSpacing: 0.3,
                fontWeight: c.key === 'overdue' ? 700 : 400,
              }}
            >
              {t.orders.summaryBar[c.key]}
            </div>
          </div>
        );
      })}
    </div>
  );
}
